/**
 * The face, painted onto one 512x256 canvas that is wrapped round the skull.
 *
 * A texture rather than geometry, because a face made of boxes at this scale
 * is a face made of boxes. Everything is placed in longitude and latitude on
 * the sphere the head is scaled out of, so a feature stays where it is put
 * however wide or tall the skull it goes on.
 *
 * Rendering only. Nothing in this file may ever be imported by `src/sim`.
 */

import * as THREE from 'three';
import {
  H,
  IRISES,
  W,
  css,
  deg,
  px,
  py,
  type Ctx,
  type HeadSpec,
  type HairStyle,
  type Rng,
} from './Figure';
export function drawFace(spec: HeadSpec): THREE.Texture {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = css(spec.skin);
    ctx.fillRect(0, 0, W, H);
    paintShading(ctx);
    if (spec.peeled) paintPeel(ctx, spec.rng);
    paintHair(ctx, spec);
    if (spec.stubble) paintStubble(ctx, spec);
    paintBrows(ctx, spec);
    paintEyes(ctx, spec);
    paintNostrils(ctx);
    paintMouth(ctx, spec);
    if (spec.moustache) paintMoustache(ctx, spec);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

/**
 * Eye sockets, the hollows under the cheekbones, and the underside of the
 * jaw: the little that a flat-lit ball needs to stop being a ball. Faint on
 * purpose - drawn any darker, sockets are a panda.
 */
function paintShading(ctx: Ctx): void {
  for (const side of [-1, 1]) {
    ctx.fillStyle = 'rgba(45, 22, 18, 0.12)';
    ellipse(ctx, px(side * 20), py(87), deg(16), deg(10));
    ctx.fillStyle = 'rgba(45, 22, 18, 0.07)';
    ellipse(ctx, px(side * 34), py(114), deg(11), deg(15));
  }
  const shade = ctx.createLinearGradient(0, py(138), 0, py(168));
  shade.addColorStop(0, 'rgba(45, 22, 18, 0)');
  shade.addColorStop(1, 'rgba(45, 22, 18, 0.3)');
  ctx.fillStyle = shade;
  ctx.fillRect(0, py(138), W, H - py(138));
}

/** Sheets of skin gone, the raw pink underneath, and a dark rim where they tore. */
function paintPeel(ctx: Ctx, rng: Rng): void {
  const spots: [number, number, number][] = [
    [-14, 54, 9],
    [36, 104, 8],
    [-40, 130, 7],
    [12, 142, 5],
    [76, 92, 8],
    [-84, 82, 7],
    [170, 100, 10],
  ];
  ctx.lineWidth = 2;
  ctx.strokeStyle = '#a3473f';
  ctx.fillStyle = '#e9a79b';
  for (const [lon, lat, radius] of spots) {
    blob(ctx, px(lon), py(lat), deg(radius), rng);
    ctx.fill();
    ctx.stroke();
  }
}

/**
 * The hair that is flat: everything above the hairline, painted pixel by
 * pixel because the hairline is a rule about longitude and latitude, not a
 * shape the canvas knows how to fill. The edge wanders by a couple of
 * degrees and each column is a slightly different shade, which is what
 * separates hair from a painted-on swimming cap.
 */
function paintHair(ctx: Ctx, spec: HeadSpec): void {
  const [hr, hg, hb] = rgb(spec.hair);
  const edge = new Float32Array(W);
  const shade = new Float32Array(W);
  let wobble = 0;
  for (let x = 0; x < W; x++) {
    wobble = wobble * 0.6 + (spec.rng() - 0.5) * 2.4;
    edge[x] = wobble;
    shade[x] = 0.8 + spec.rng() * 0.4;
  }

  // A buzz cut is scalp seen through hair, not hair.
  const cover = spec.style === 'buzz' ? 0.55 : 1;
  const image = ctx.getImageData(0, 0, W, H);
  const d = image.data;
  for (let x = 0; x < W; x++) {
    let lon = (x / W - 0.25) * 360;
    if (lon > 180) lon -= 360;
    const k = (shade[x] + shade[(x + 1) % W] + shade[(x + W - 1) % W]) / 3;
    for (let y = 0; y < H; y++) {
      const lat = (y / H) * 180;
      if (!inHair(spec.style, lon, lat, edge[x])) continue;
      const i = (y * W + x) * 4;
      d[i] += (Math.min(255, hr * k) - d[i]) * cover;
      d[i + 1] += (Math.min(255, hg * k) - d[i + 1]) * cover;
      d[i + 2] += (Math.min(255, hb * k) - d[i + 2]) * cover;
    }
  }
  ctx.putImageData(image, 0, 0);
}

/**
 * Whether a point on the scalp has hair on it. `jitter` is this column's
 * wander of the hairline, in degrees.
 *
 * The rule is the same shape for every style but `bald`: a hairline across
 * the forehead, turning down in front of the ear into a sideburn, and the
 * back covered from the crown to the nape. The styles differ in where those
 * three lines sit, and in what the front line does in the middle.
 */
function inHair(style: HairStyle, lon: number, lat: number, jitter: number): boolean {
  const a = Math.abs(lon);
  if (style === 'bald') {
    // The horseshoe: sides and back, and nothing on top.
    return a >= 45 && lat >= 68 + jitter && lat <= 108 + jitter;
  }
  const nape = style === 'long' ? 135 : style === 'buzz' ? 104 : 112;
  const sideburn = style === 'long' ? 96 : style === 'receding' ? 78 : 86;
  let front: number;
  switch (style) {
    case 'long':
      // A fringe, hanging lowest in the middle.
      front = 38 + 9 * Math.max(0, 1 - a / 25);
      break;
    case 'receding':
      // The temples gone, and a peak left in the middle.
      front = 36 - 13 * Math.sin((Math.min(a, 50) / 50) * Math.PI);
      break;
    default:
      front = 38;
  }
  let limit: number;
  if (a < 50) limit = front;
  else if (a < 60) limit = front + ((sideburn - front) * (a - 50)) / 10;
  else if (a < 80) limit = sideburn;
  else limit = nape;
  return lat <= limit + jitter;
}

/** A day's growth: a faint wash over the jaw with the odd darker fleck in it. */
function paintStubble(ctx: Ctx, spec: HeadSpec): void {
  const [r, g, b] = rgb(spec.hair);
  ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.14)`;
  ellipse(ctx, px(0), py(133), deg(50), deg(24));
  ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.4)`;
  for (let i = 0; i < 280; i++) {
    const lon = (spec.rng() - 0.5) * 100;
    const lat = 109 + spec.rng() * 48;
    if (Math.hypot(lon / 50, (lat - 133) / 24) > 0.95) continue;
    ctx.fillRect(px(lon), py(lat), 1.3, 1.3);
  }
}

function paintBrows(ctx: Ctx, spec: HeadSpec): void {
  const delighted = spec.expression === 'delighted';
  ctx.strokeStyle = css(spec.hair, 0.7);
  ctx.lineCap = 'round';
  ctx.lineWidth = deg(delighted ? 3.2 : 2.3);
  for (const side of [-1, 1]) {
    ctx.beginPath();
    if (spec.expression === 'angry') {
      ctx.moveTo(px(side * 7), py(85));
      ctx.lineTo(px(side * 33), py(73));
    } else if (spec.expression === 'sad') {
      ctx.moveTo(px(side * 7), py(70));
      ctx.lineTo(px(side * 33), py(82));
    } else if (delighted) {
      // Up and arched, and well clear of the eye. Brows pulled down over the
      // nose say "angry" whatever the mouth underneath them is doing, so on a
      // face that is meant to be pleased to see you they have to go the other
      // way - and go there far enough to be read at a run.
      ctx.moveTo(px(side * 7), py(72));
      ctx.quadraticCurveTo(px(side * 20), py(65.5), px(side * 33), py(71));
    } else {
      ctx.moveTo(px(side * 8), py(77));
      ctx.quadraticCurveTo(px(side * 20), py(73.5), px(side * 32), py(75.5));
    }
    ctx.stroke();
  }
}

function paintEyes(ctx: Ctx, spec: HeadSpec): void {
  const lat = 88;
  if (spec.expression === 'asleep') {
    ctx.lineCap = 'round';
    for (const side of [-1, 1]) {
      const cx = px(side * 20);
      const cy = py(lat);
      // The lid closed, bowing down, and the crease above it.
      ctx.strokeStyle = 'rgba(35, 20, 15, 0.85)';
      ctx.lineWidth = 2.2;
      ctx.beginPath();
      ctx.moveTo(cx - deg(11), cy - deg(1));
      ctx.quadraticCurveTo(cx, cy + deg(5), cx + deg(11), cy - deg(1));
      ctx.stroke();
      ctx.strokeStyle = 'rgba(35, 20, 15, 0.3)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(cx - deg(9), cy - deg(5));
      ctx.quadraticCurveTo(cx, cy - deg(2), cx + deg(9), cy - deg(5));
      ctx.stroke();
    }
    return;
  }

  const delighted = spec.expression === 'delighted';
  // A smile squeezes the eye from below, so a delighted one is shorter than an
  // awake one and no wider: a wide eye over a wide-open mouth is alarm.
  const rx = deg(11);
  const ry = deg(spec.expression === 'angry' ? 3.2 : delighted ? 4.6 : 5.5);
  // Both eyes look the same way; a stare picks it once. A man who is pleased
  // to see you is looking at you, so his is not picked at all.
  const gaze = delighted || spec.expression === 'angry' || spec.expression === 'sad' ? 0 : (spec.rng() - 0.5) * deg(5);
  const iris = IRISES[(spec.rng() * IRISES.length) | 0];
  for (const side of [-1, 1]) {
    const cx = px(side * 20);
    const cy = py(lat);
    ctx.fillStyle = '#efe9e2';
    ellipse(ctx, cx, cy, rx, ry);
    const irisR = deg(delighted ? 4.2 : 5);
    ctx.fillStyle = css(iris);
    ellipse(ctx, cx + gaze, cy + deg(0.4), irisR, irisR);
    ctx.fillStyle = '#100c0a';
    ellipse(ctx, cx + gaze, cy + deg(0.4), deg(2.4), deg(2.4));
    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ellipse(ctx, cx + gaze - deg(1.4), cy - deg(1.2), deg(1), deg(1));
    // The upper lid over it all.
    ctx.strokeStyle = 'rgba(35, 20, 15, 0.9)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(cx - rx, cy);
    ctx.quadraticCurveTo(cx, cy - ry * 1.5, cx + rx, cy);
    ctx.stroke();
    if (!delighted) continue;
    // The cheek pushed up under the eye, and the creases it puts at the outer
    // corner. At the distance the eyes are two dots these are the whole smile:
    // an open mouth on its own, that far off, is a man shouting.
    ctx.strokeStyle = css(spec.skin, 0.72);
    ctx.lineCap = 'round';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(cx - rx * 0.95, cy + deg(6.5));
    ctx.quadraticCurveTo(cx, cy + deg(2.5), cx + rx * 0.95, cy + deg(6.5));
    ctx.stroke();
    ctx.lineWidth = 1.2;
    for (let i = -1; i <= 1; i++) {
      ctx.beginPath();
      ctx.moveTo(cx + side * rx * 0.9, cy + deg(i * 1.6));
      ctx.lineTo(cx + side * (rx + deg(3.6)), cy + deg(i * 4.4));
      ctx.stroke();
    }
  }
}

/** Two dots just under where the nose cone ends. */
function paintNostrils(ctx: Ctx): void {
  ctx.fillStyle = 'rgba(40, 20, 15, 0.6)';
  for (const side of [-1, 1]) ellipse(ctx, px(side * 5), py(109), deg(1.7), deg(1.3));
}

function paintMouth(ctx: Ctx, spec: HeadSpec): void {
  const cx = px(0);
  switch (spec.expression) {
    case 'sad':
    case 'angry': {
      ctx.strokeStyle = '#57251e';
      ctx.lineCap = 'round';
      ctx.lineWidth = deg(3);
      ctx.beginPath();
      ctx.moveTo(px(-23), py(130));
      ctx.quadraticCurveTo(cx, py(116), px(23), py(130));
      ctx.stroke();
      break;
    }
    case 'delighted': {
      // Open and laughing. The corners sit well *above* the middle of the top
      // lip, which is the whole difference between this and the shout it would
      // otherwise be - the same hole in a face reads either way, and it is the
      // few degrees of sweep at the ends that decide which.
      //
      // Wide and shallow rather than round, because the band it has to live in
      // is narrow at both ends: a moustache comes down to 121 deg, and past
      // about 133 the jaw's own sphere stands proud of the skull and swallows
      // whatever is painted there. A grin authored down onto the chin is a
      // grin nobody ever sees the bottom of.
      const corner = py(117);
      const half = deg(24);
      const mouth = () => {
        ctx.beginPath();
        ctx.moveTo(cx - half, corner);
        ctx.quadraticCurveTo(cx, corner + deg(30), cx + half, corner);
        ctx.quadraticCurveTo(cx, corner + deg(10), cx - half, corner);
        ctx.closePath();
      };
      mouth();
      ctx.fillStyle = '#3a1210';
      ctx.fill();

      // Teeth and tongue, clipped to the opening: drawn as shapes of their own
      // they stand on his chin the moment anybody widens the grin.
      ctx.save();
      ctx.clip();
      ctx.strokeStyle = '#f2ead9';
      ctx.lineWidth = deg(7);
      ctx.beginPath();
      ctx.moveTo(cx - half, corner);
      ctx.quadraticCurveTo(cx, corner + deg(10), cx + half, corner);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(60, 40, 30, 0.45)';
      ctx.lineWidth = 1;
      for (let i = -4; i <= 4; i++) {
        ctx.beginPath();
        ctx.moveTo(cx + deg(i * 5.4), corner + deg(2));
        ctx.lineTo(cx + deg(i * 5.4), corner + deg(11));
        ctx.stroke();
      }
      ctx.fillStyle = '#9c3b3b';
      ellipse(ctx, cx, corner + deg(14.5), deg(9), deg(2.4));
      ctx.restore();

      ctx.strokeStyle = css(spec.skin, 0.72);
      ctx.lineWidth = 2;
      mouth();
      ctx.stroke();

      // The corners carried on past the lip, and the fold from the nose down
      // around them. These two marks are the whole reason a moustache cannot
      // swallow the expression: the top lip is under it, and they are not.
      ctx.lineCap = 'round';
      ctx.strokeStyle = css(spec.skin, 0.6);
      ctx.lineWidth = 2.4;
      for (const side of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(px(side * 22.5), corner + deg(2));
        ctx.quadraticCurveTo(px(side * 28), py(114), px(side * 29), py(109));
        ctx.stroke();
      }
      ctx.strokeStyle = css(spec.skin, 0.82);
      ctx.lineWidth = 2;
      for (const side of [-1, 1]) {
        ctx.beginPath();
        ctx.moveTo(px(side * 11), py(104));
        ctx.quadraticCurveTo(px(side * 25), py(109), px(side * 26.5), py(119));
        ctx.stroke();
      }
      break;
    }
    case 'asleep': {
      // Slack, and a little open.
      ctx.strokeStyle = css(spec.skin, 0.62);
      ctx.lineCap = 'round';
      ctx.lineWidth = 2.2;
      ctx.beginPath();
      ctx.moveTo(px(-12), py(122.5));
      ctx.quadraticCurveTo(px(0), py(125.5), px(12), py(122.5));
      ctx.stroke();
      ctx.fillStyle = '#4a2a25';
      ellipse(ctx, cx + deg(1.5), py(125.5), deg(7), deg(2.8));
      break;
    }
    case 'awake': {
      ctx.strokeStyle = css(spec.skin, 0.6);
      ctx.lineCap = 'round';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(px(-11), py(123));
      ctx.quadraticCurveTo(px(0), py(125), px(11), py(123));
      ctx.stroke();
      ctx.strokeStyle = css(spec.skin, 1.12);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(px(-8), py(126.5));
      ctx.quadraticCurveTo(px(0), py(128), px(8), py(126.5));
      ctx.stroke();
      break;
    }
  }
}

/** Under the nose and over the lip, and drooping at the ends. */
function paintMoustache(ctx: Ctx, spec: HeadSpec): void {
  ctx.fillStyle = css(spec.hair, 0.85);
  ctx.beginPath();
  ctx.moveTo(px(-18), py(121));
  ctx.quadraticCurveTo(px(-8), py(110.5), px(0), py(112.5));
  ctx.quadraticCurveTo(px(8), py(110.5), px(18), py(121));
  ctx.quadraticCurveTo(px(8), py(119), px(0), py(119.5));
  ctx.quadraticCurveTo(px(-8), py(119), px(-18), py(121));
  ctx.fill();
  ctx.strokeStyle = css(spec.hair, 1.35);
  ctx.lineWidth = 1;
  for (let i = 0; i < 10; i++) {
    const lon = (spec.rng() - 0.5) * 30;
    ctx.beginPath();
    ctx.moveTo(px(lon), py(113.5));
    ctx.lineTo(px(lon + Math.sign(lon) * 2), py(118.5));
    ctx.stroke();
  }
}

export function rgb(hex: number): [number, number, number] {
  return [(hex >> 16) & 255, (hex >> 8) & 255, hex & 255];
}

function ellipse(ctx: Ctx, x: number, y: number, rx: number, ry: number): void {
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
}

/** A rough-edged patch: a circle whose radius changes its mind nine times round. */
function blob(ctx: Ctx, x: number, y: number, radius: number, rng: Rng): void {
  const n = 9;
  const radii: number[] = [];
  for (let i = 0; i < n; i++) radii.push(radius * (0.7 + rng() * 0.6));
  ctx.beginPath();
  for (let i = 0; i <= n; i++) {
    const t = ((i % n) / n) * Math.PI * 2;
    const k = radii[i % n];
    const point = [x + Math.cos(t) * k, y + Math.sin(t) * k];
    if (i === 0) ctx.moveTo(point[0], point[1]);
    else ctx.lineTo(point[0], point[1]);
  }
  ctx.closePath();
}
